import { createSelector } from "@reduxjs/toolkit"

export const selectAllUsers = (state) => {
  return state.common.allUsers
}

export const selectPopulation = (state) => state.common.population

export const selectGameConfig = (state) => {
  return state.common?.gameConfig
}

// The OAuth client ID comes from the server, not from a REACT_APP_ variable: the backend checks the
// same string as the token's audience, and two places to configure it is one place to get it wrong.
// Empty means this server offers no Google sign-in, and the button does not render.
export const selectGoogleClientId = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.google_client_id || ""
})

// The key a browser subscribes to round-ready pushes with (`utils/pushNotifications`). From the
// server because the server signs every push with the other half of the pair; empty means this
// server sends none, and the settings row does not render.
export const selectPushPublicKey = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.push_public_key || ""
})

// How this server takes money: which processor, and the public client ID its checkout needs. From
// the server for the same reason Google's client ID is — the browser renders the buttons with it
// and the server holds the secret for the same merchant account, so two places to configure it is
// one place for a payment to be opened against the wrong merchant.
//
// A block naming its provider rather than a `paypal_*` key: `Checkout` switches on `provider`, so
// a second processor is a new branch there and not a new key every screen has to learn about.
export const selectPaymentsConfig = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.payments || null
})

export const selectPaymentProvider = createSelector(
  selectPaymentsConfig,
  (payments) => payments?.provider || ""
)

// Empty means this server sells nothing, and the checkout does not render.
export const selectPaymentClientId = createSelector(
  selectPaymentsConfig,
  (payments) => payments?.client_id || ""
)

// How long the server makes a player wait between reactions. Read rather than assumed, for the
// same reason as every price: the button greys out for exactly as long as the server will refuse,
// so it never comes back early into a refusal the player did nothing to earn. Falls back to the
// server's own default for a payload from before it was published.
export const selectReactionCooldownMs = createSelector(
  selectGameConfig,
  (gameConfig) => (gameConfig?.reaction_cooldown_seconds ?? 2) * 1000
)

// The ranking's leagues (owner, 2026-10-08): `{leagues: [{id, min_points}], commander_top, commander_min_league}`, read by
// `utils/ranking.leagueFor`. Null on a server from before it was published, and no badge is drawn.
export const selectRankingLeagues = createSelector(selectGameConfig, (gameConfig) => gameConfig?.ranking_leagues ?? null)

// The longest lobby password the server stores; the create dialog's field stops there. Null on a
// server from before it was published, which then sets no limit and answers a long one with a 400.
export const selectLobbyPasswordLimit = createSelector(
  selectGameConfig,
  (gameConfig) => gameConfig?.lobby_password_limit ?? null
)

// How to reach the developer and where the players gather, from the server's config so a new
// account needs no rebuild. Only the links the server actually names are returned, so nothing draws
// a dead one. The address is published bare and becomes a mailto here.
export const selectCommunityLinks = createSelector(selectGameConfig, (gameConfig) => {
  const community = gameConfig?.community || {}
  return ["email", "instagram", "tiktok", "discord"]
    .filter((network) => community[network])
    .map((network) => ({
      network,
      url: network === "email" ? `mailto:${community[network]}` : community[network],
    }))
})

// Every price the shop shows, published by the same endpoint as the balance constants — a price
// on a button is a claim about what the server will charge.
export const selectEconomyConfig = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.economy || null
})

// The modes this server will open a lobby in, in the server's own order. The create and find
// dialogs list what comes back rather than a copy: a mode the server has and the bundle has not is
// a mode nobody can pick, and a mode the bundle offers and the server refuses is worse.
export const selectGameModes = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.game_modes || []
})

// What each mode seats (SV2): `{sides, seats_per_side, min_seats_per_side, min_seats}` per mode.
// The lobby decides
// whether a room is complete off this rather than off `membersPerTeam x 2`, which is an answer
// only a two-sided mode can give — the same table the server's own seat rules read, published for
// the same reason every balance number is. Null on a server from before it was published, which
// the lobby falls back from rather than crashing on.
export const selectGameModeRules = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.game_mode_rules || null
})

// Survival's whole tuning table (SV6) — seats, the wave cadence and the tier ladder. The lobby
// quotes the cadence off this and never off a constant of its own.
export const selectSurvivalConfig = createSelector(selectGameConfig, (gameConfig) => {
  return gameConfig?.survival || null
})

// The map sizes this server offers (requirement 35, M11): `[{size, columns, rows, crowns}]` in
// S, M, L order, off `map_sizes` in GET /game-config/. The create and find dialogs list these and
// word each by its crowns, so a choice on screen is the server's own row. Empty on a server from
// before the sizes, which offers L alone and the dialogs then show no size at all.
const MAP_SIZE_ORDER = ["S", "M", "L"]
export const selectMapSizes = createSelector(selectGameConfig, (gameConfig) => {
  const published = gameConfig?.map_sizes
  if (!published || typeof published !== "object") return []
  return MAP_SIZE_ORDER.filter((size) => published[size]).map((size) => ({ size, ...published[size] }))
})
